export interface ModuleAccessEntry {
  m?: unknown;
}

export interface ModuleDefinition {
  id: number;
  routePath: string;
  label: string;
  icon: string;
  navigateTo: string;
}

export const NAV_OPTIONS_ORDER: number[] = [
  1, 5, 23, 24, 25, 26, 2, 3, 16, 9, 4, 7, 19, 10, 11, 17, 21, 12, 13, 14, 15, 6, 18, 100, 101,
];

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  { id: 1, routePath: "home", label: "MENU.HOME", icon: "home", navigateTo: "/web/home" },
  { id: 2, routePath: "system", label: "MENU.SYSTEM", icon: "account_tree", navigateTo: "/web/system" },
  { id: 3, routePath: "analysis", label: "MENU.ANALYSIS", icon: "query_stats", navigateTo: "/web/analysis" },
  { id: 4, routePath: "configuration", label: "MENU.CONFIGURATION", icon: "tune", navigateTo: "/web/configuration" },
  { id: 5, routePath: "alert", label: "MENU.ALERTS", icon: "warning", navigateTo: "/web/alert" },
  { id: 23, routePath: "alert-triage", label: "MENU.ALERT_TRIAGE", icon: "rule", navigateTo: "/web/alert-triage" },
  { id: 24, routePath: "knowledge-graph", label: "MENU.KNOWLEDGE_GRAPH", icon: "hub", navigateTo: "/web/knowledge-graph" },
  { id: 25, routePath: "asset-explorer", label: "MENU.ASSET_EXPLORER", icon: "account_tree", navigateTo: "/web/asset-explorer" },
  { id: 26, routePath: "library", label: "MENU.LIBRARY", icon: "local_library", navigateTo: "/web/library" },
  { id: 6, routePath: "user-management", label: "MENU.USER_MANAGEMENT", icon: "manage_accounts", navigateTo: "/web/user-management" },
  { id: 7, routePath: "model-wizard", label: "MENU.MODEL_WIZARD", icon: "model_training", navigateTo: "/web/model-wizard" },
  { id: 9, routePath: "iow", label: "MENU.IOW", icon: "monitoring", navigateTo: "/web/iow" },
  { id: 10, routePath: "process", label: "MENU.PROCESS", icon: "process_chart", navigateTo: "/web/process" },
  { id: 11, routePath: "genai", label: "MENU.GENAI", icon: "network_intel_node", navigateTo: "/web/genai" },
  { id: 12, routePath: "maintenance", label: "MENU.MAINTENANCE_OPTIMIZATION", icon: "factory", navigateTo: "/web/maintenance" },
  { id: 13, routePath: "rcm", label: "MENU.RCM", icon: "analytics", navigateTo: "/web/rcm" },
  { id: 14, routePath: "platform-settings", label: "MENU.SETTINGS", icon: "settings", navigateTo: "/web/platform-settings" },
  { id: 15, routePath: "hazop", label: "MENU.HAZOP", icon: "report", navigateTo: "/web/hazop" },
  { id: 16, routePath: "case", label: "MENU.CASES", icon: "home_storage", navigateTo: "/web/case" },
  { id: 17, routePath: "capacity-optimization", label: "MENU.CAPACITY_OPTIMIZATION", icon: "precision_manufacturing", navigateTo: "/web/capacity-optimization" },
  { id: 18, routePath: "health-metrics", label: "MENU.HEALTH_METRICS", icon: "dns", navigateTo: "/web/health-metrics" },
  { id: 19, routePath: "widget", label: "MENU.DASHBOARD_WIZARD", icon: "dashboard_customize", navigateTo: "/web/widget" },
  { id: 101, routePath: "maintenance-optimization", label: "MENU.MAINTENANCE_OPTIMIZATION", icon: "build", navigateTo: "/web/maintenance-optimization" },
  { id: 21, routePath: "what-if", label: "MENU.WHAT_IF", icon: "help_center", navigateTo: "/web/what-if" },
];

const MODULE_ID_TO_ROUTE_PATH = new Map<number, string>(
  MODULE_DEFINITIONS.map((module) => [module.id, module.routePath]),
);

const MODULE_ROUTE_PATH_TO_ID = new Map<string, number>(
  MODULE_DEFINITIONS.map((module) => [module.routePath, module.id]),
);

export const normalizeModuleIds = (moduleIds: unknown[] = []): number[] =>
  moduleIds.map((module) => Number(module)).filter((module) => Number.isFinite(module));

export const getUserModuleIds = (moduleAccess: ModuleAccessEntry[] = []): number[] =>
  normalizeModuleIds(moduleAccess.map((module) => module?.m));

export const getRoutePathFromModuleId = (moduleId?: unknown): string => {
  const normalizedModuleId = Number(moduleId);
  if (!Number.isFinite(normalizedModuleId)) {
    return "";
  }
  return MODULE_ID_TO_ROUTE_PATH.get(normalizedModuleId) || "";
};

export const getModuleIdFromRoutePath = (routePath = ""): number | undefined =>
  MODULE_ROUTE_PATH_TO_ID.get(routePath);

export const isModuleRoutePath = (routePath = ""): boolean => MODULE_ROUTE_PATH_TO_ID.has(routePath);

export const getRouteSegmentFromUrl = (url = ""): string => {
  const [pathPart] = url.split(/[?#]/);
  const segments = pathPart.split("/").filter(Boolean);
  const webIndex = segments.indexOf("web");
  if (webIndex === -1) {
    return segments[0] || "";
  }
  return segments[webIndex + 1] || "";
};

export const getAccessibleModuleIds = (
  userModuleAccess: ModuleAccessEntry[] = [],
  tenantModuleAccess: unknown[] = [],
): number[] => {
  const userModuleIds = getUserModuleIds(userModuleAccess);
  const tenantModuleIds = normalizeModuleIds(tenantModuleAccess);
  const triageIds = [23, 24, 25, 26];
  if (!tenantModuleIds.length) {
    return Array.from(new Set([...userModuleIds, ...triageIds]));
  }
  const filtered = userModuleIds.filter((moduleId) => tenantModuleIds.includes(moduleId));
  return Array.from(new Set([...filtered, ...triageIds]));
};

export const hasModuleRouteAccess = (
  routePath: string,
  userModuleAccess: ModuleAccessEntry[] = [],
  tenantModuleAccess: unknown[] = [],
): boolean => {
  if (['alert-triage', 'asset-explorer', 'knowledge-graph', 'library'].includes(routePath)) {
    return true;
  }
  const moduleId = getModuleIdFromRoutePath(routePath);
  if (!moduleId) {
    return false;
  }
  return getAccessibleModuleIds(userModuleAccess, tenantModuleAccess).includes(moduleId);
};

export const getFirstAvailableModulePath = (
  userModuleAccess: ModuleAccessEntry[] = [],
  tenantModuleAccess: unknown[] = [],
): string => {
  const activeModuleIds = getAccessibleModuleIds(userModuleAccess, tenantModuleAccess);
  const orderedAccessibleModule = NAV_OPTIONS_ORDER.find((moduleId) => activeModuleIds.includes(moduleId));
  const fallbackModule = orderedAccessibleModule ?? activeModuleIds[0];
  return getRoutePathFromModuleId(fallbackModule) || "system";
};

export const getFirstAvailableModuleUrl = (
  userModuleAccess: ModuleAccessEntry[] = [],
  tenantModuleAccess: unknown[] = [],
): string => `/web/${getFirstAvailableModulePath(userModuleAccess, tenantModuleAccess)}`;
